/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import _ from 'lodash'
import Modal from '../../Modal/index'
import { Select } from '../../Select/Select'
import { useServiceContext } from '../../../root'
import { transformToSelectOptions } from '../../../utils/selectOptions'
import { parseTriggerNameOrRoles, getSubTriggerNameOrPH } from './utils'
import { TRIGGER, MAX_SELECTION_ALLOWED } from './constants'
import type { PackageData, PackageOptions, TriggerOption, SelectedFilters } from './types'

interface EditPackageStep1Props {
  packageData: PackageData | null
  options: PackageOptions | null
  nextStep: (packageData: PackageData, optionsData: PackageOptions, nextStepItem: 'STEP1' | 'STEP2' | 'STEP3') => void
  doClose: () => void
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>
  triggersNames: TriggerOption[]
  isLoading: boolean
}

interface FormValues {
  name: string
  level: string
  filePlan: { label: string; value: number | null }
  autoApplyPackage?: boolean
  triggerName?: { label: string; value: string }
  triggerDetails?: Array<{ label: string; value: string }> | string | null
}

const EditPackageStep1: React.FC<EditPackageStep1Props> = ({
  packageData,
  options,
  nextStep,
  doClose,
  setSelectedFilters,
  triggersNames,
  isLoading
}) => {
  const context = useServiceContext()
  const formRef = useRef<any>(null)
  const triggerDetailsOptsRef = useRef<Record<string, Array<{ label: string; value: string }>>>({})
  const { eventTriggers } = context?.service?.init?.getFeaturesInfo?.() || {}
  const { api } = context
  const translate = context.service.filter.translate
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  const [formValues, setFormValues] = useState<FormValues>({
    name: '',
    level: '',
    filePlan: { label: '', value: null }
  })
  const [isFilePlanInvalid, setIsfilePlanInvalid] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [triggerOptions, setTriggerOptions] = useState<Array<{ label: string; value: string }>>(
    packageData?.triggerName && typeof packageData.triggerName === 'object' && packageData.triggerName.value
      ? triggerDetailsOptsRef.current[packageData.triggerName.value] || []
      : []
  )

  useEffect(() => {
    const triggerValue =
      typeof packageData?.triggerName === 'string' ? packageData.triggerName : packageData?.triggerName?.value
    if (packageData?.autoApplyPackage && triggerValue) {
      onTriggerNameChange(triggerValue, packageData?.level)
    }

    const baseValues: FormValues = {
      name: packageData?.name ?? '',
      level: packageData?.level ?? 'STUDY',
      filePlan: {
        value: packageData?.filePlanId ?? null,
        label: packageData?.filePlanName ?? ''
      }
    }

    if (packageData?.autoApplyPackage) {
      baseValues.autoApplyPackage = packageData.autoApplyPackage

      if (packageData?.triggerName) {
        baseValues.triggerName =
          typeof packageData.triggerName === 'string'
            ? { label: translate(`TRIGGER_${packageData.triggerName}`), value: packageData.triggerName }
            : packageData.triggerName
      }

      if (packageData?.triggerDetails) {
        const triggerValue =
          typeof packageData.triggerName === 'string' ? packageData.triggerName : packageData.triggerName?.value

        if (triggerValue === TRIGGER.TEAMS || triggerValue === TRIGGER.MILESTONES) {
          if (Array.isArray(packageData.triggerDetails)) {
            baseValues.triggerDetails = packageData.triggerDetails
          } else if (typeof packageData.triggerDetails === 'object' && 'triggerDetail' in packageData.triggerDetails) {
            baseValues.triggerDetails = parseTriggerNameOrRoles(
              true,
              packageData.triggerDetails.triggerDetail?.attributeValues
            ) as Array<{ label: string; value: string }>
          } else {
            baseValues.triggerDetails = []
          }
        } else {
          if (typeof packageData.triggerDetails === 'string') {
            baseValues.triggerDetails = packageData.triggerDetails
          } else if (typeof packageData.triggerDetails === 'object' && 'triggerDetail' in packageData.triggerDetails) {
            baseValues.triggerDetails = packageData.triggerDetails.triggerDetail?.attributeValues?.[0] || ''
          } else {
            baseValues.triggerDetails = ''
          }
        }
      }
    }

    setFormValues(baseValues)
  }, [packageData, options])

  useEffect(() => {
    if (packageData?.triggerName === TRIGGER.MILESTONES && packageData?.level) {
      fetchCTMSMilestonesReachedTriggerData(packageData.level)
    }
  }, [packageData?.level])

  const gotoStep2 = () => {
    setIsSubmitted(true)
    formRef.current.validateForm().then(() => {
      const errors = formRef.current.errors
      if (!Object.keys(errors).length) {
        proceedToStep2(formRef?.current?.values)
      }
    })
  }

  const proceedToStep2 = (formData: FormValues) => {
    if (!packageData) return
    const packageDataLocal = cloneDeep(packageData) as any
    packageDataLocal.name = formData?.name
    packageDataLocal.filePlanId = formData.filePlan.value
    packageDataLocal.filePlanName = formData.filePlan.label
    packageDataLocal.level = formData.level
    if (eventTriggers?.length) {
      packageDataLocal.autoApplyPackage = formData?.autoApplyPackage || false
      formData?.autoApplyPackage
        ? (packageDataLocal.triggerName = formData?.triggerName)
        : delete packageDataLocal.triggerName
      formData?.autoApplyPackage
        ? (packageDataLocal.triggerDetails = formData?.triggerDetails || undefined)
        : delete packageDataLocal.triggerDetails
    }

    if (
      !options?.fileplanEntries ||
      packageDataLocal?.filePlanId !== options?.loadedFileplan ||
      packageDataLocal?.level !== options?.loadedLevel
    ) {
      const isFilePlanChanged = !!(packageDataLocal?.filePlanId != options?.packageCopy?.filePlanId)
      api.packages
        .getEntriesForSelector(
          packageDataLocal?.filePlanId,
          packageDataLocal?.level,
          packageDataLocal?.entryIds,
          options?.isClone,
          isFilePlanChanged,
          packageDataLocal?.entryNames
        )
        .then(
          (fileplanEntries) => {
            let optionsLocal = cloneDeep(options)
            optionsLocal = _.extend(options, {
              filePlanLevel3Entries: api.packages.getLevel3Entries(fileplanEntries),
              fileplanEntries: fileplanEntries,
              loadedFileplan: packageDataLocal.filePlanId,
              loadedLevel: packageDataLocal.level
            })
            setIsfilePlanInvalid(false)
            nextStep(packageDataLocal, optionsLocal, 'STEP2')
          },
          () => {
            setIsfilePlanInvalid(true)
          }
        )
    } else {
      nextStep(packageDataLocal, options, 'STEP2')
    }
  }

  function processedTriggerDetailOptions(
    options: Array<{ label: string; value: string }>,
    selectedOptions: Array<{ label: string; value: string }> | undefined
  ) {
    return selectedOptions?.length && selectedOptions.length >= MAX_SELECTION_ALLOWED ? selectedOptions : options
  }

  function fetchAndSetVisitTypeCategories() {
    api.packages.getVisitTypeCategory().then((resp) => {
      if (resp?.visitTypeCategories) {
        const visitsOptions = parseTriggerNameOrRoles(true, resp.visitTypeCategories)
        triggerDetailsOptsRef.current.ctmsVisits = visitsOptions as Array<{ label: string; value: string }>
        setTriggerOptions(visitsOptions as Array<{ label: string; value: string }>)
      }
    })
  }

  function fetchAndSetTeamsRoles() {
    api.packages.getTeamRolesForTrigger().then((resp) => {
      if (resp?.teamMembers) {
        const teamsOptions = parseTriggerNameOrRoles(true, resp.teamMembers)
        triggerDetailsOptsRef.current.teams = teamsOptions as Array<{ label: string; value: string }>
        setTriggerOptions(teamsOptions as Array<{ label: string; value: string }>)
      }
    })
  }

  function fetchCTMSMilestonesReachedTriggerData(level: string) {
    if (level) {
      api.packages.getCTMSMilestonesReachedForTrigger(capitalize(level)).then(
        (resp) => {
          const ctmsMilestoneData = resp?.ctmsMilestone
          if (ctmsMilestoneData && Array.isArray(ctmsMilestoneData) && ctmsMilestoneData.length) {
            const ctmsMilestone = parseTriggerNameOrRoles(true, ctmsMilestoneData as string[])
            triggerDetailsOptsRef.current.ctmsMilestone = ctmsMilestone as Array<{ label: string; value: string }>
            setTriggerOptions(ctmsMilestone as Array<{ label: string; value: string }>)
          } else {
            // Clear options when API returns empty data to prevent showing stale data
            triggerDetailsOptsRef.current.ctmsMilestone = []
            setTriggerOptions([])
          }
        },
        (err) => {
          context.service.toast.error(err?.data?.message ?? 'ERROR')
        }
      )
    } else {
      // Clear options when no level is provided
      setTriggerOptions([])
    }
  }

  function onTriggerNameChange(selectedTrigger: string, level?: string) {
    if (selectedTrigger === TRIGGER.VISITS) {
      if (triggerDetailsOptsRef.current?.ctmsVisits?.length) {
        setTriggerOptions(triggerDetailsOptsRef.current?.ctmsVisits)
      } else {
        fetchAndSetVisitTypeCategories()
      }
    } else if (selectedTrigger === TRIGGER.TEAMS) {
      if (triggerDetailsOptsRef.current?.teams?.length) {
        setTriggerOptions(triggerDetailsOptsRef.current?.teams)
      } else {
        fetchAndSetTeamsRoles()
      }
    } else if (selectedTrigger === TRIGGER.MILESTONES) {
      if (triggerDetailsOptsRef.current?.ctmsMilestone?.length) {
        setTriggerOptions(triggerDetailsOptsRef.current?.ctmsMilestone)
      } else {
        fetchCTMSMilestonesReachedTriggerData(level || '')
      }
    }
  }

  const reFetchTriggerDetails = (triggerName: string | undefined, level: string, setFieldValue: any) => {
    if (!triggerName || triggerName === TRIGGER.MILESTONES) {
      setFieldValue('triggerDetails', [])
      fetchCTMSMilestonesReachedTriggerData(level)
    }
  }

  return (
    <>
      <Modal.Body>
        <Formik
          initialValues={formValues}
          innerRef={formRef}
          enableReinitialize
          validateOnChange
          onSubmit={() => {}}
          validate={(values) => {
            const errors: Record<string, string> = {}
            if (values?.name === '') {
              errors.name = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
            } else {
              let isNameValid
              if (options?.isClone) {
                isNameValid = !options?.packagesForPicker?.find((p: any) => p.name === values?.name)
              } else {
                isNameValid =
                  options?.packageCopy?.name === values?.name ||
                  !options?.packagesForPicker?.find((p: any) => p.name === values?.name)
              }
              if (!isNameValid) {
                errors.name = 'ERROR_NAME_ALREADY_EXISTS_MSG'
              }
            }
            if (!values?.filePlan?.value) {
              errors.filePlan = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
            }

            if (eventTriggers?.length && values?.autoApplyPackage) {
              if (values?.triggerName?.value) {
                if (values?.triggerName?.value === TRIGGER.TEAMS || values?.triggerName?.value === TRIGGER.MILESTONES) {
                  !(Array.isArray(values?.triggerDetails) && values.triggerDetails?.length)
                    ? (errors.triggerDetails = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
                    : null
                } else {
                  !values?.triggerDetails ? (errors.triggerDetails = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR') : null
                }
              } else {
                errors.triggerName = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
            }

            return errors
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <Form>
              <div className="form-group w50p">
                <label className="control-label">{translate('Name')}</label>
                <Field type="text" className="form-control" value={values?.name} name="name" maxLength="500" />
                {isSubmitted && errors?.name && <div className="error">{translate(errors?.name)}</div>}
              </div>
              <div className="form-group">
                <label className="control-label">{translate('EMTF_LEVEL')}</label>
                <div>
                  <div className="radio">
                    <label>
                      <Field
                        type="radio"
                        checked={values?.level === 'STUDY'}
                        value="STUDY"
                        name="level"
                        onClick={() => {
                          setFieldValue('level', 'STUDY')
                          reFetchTriggerDetails(values?.triggerName?.value, 'STUDY', setFieldValue)
                        }}
                      />
                      {translate('EMTF_MD_STUDY')}
                    </label>
                  </div>
                  <div className="radio">
                    <label>
                      <Field
                        type="radio"
                        checked={values?.level === 'COUNTRY'}
                        value="COUNTRY"
                        name="level"
                        onClick={() => {
                          setFieldValue('level', 'COUNTRY')
                          reFetchTriggerDetails(values?.triggerName?.value, 'COUNTRY', setFieldValue)
                        }}
                      />
                      {translate('EMTF_MD_COUNTRY')}
                    </label>
                  </div>
                  <div className="radio">
                    <label>
                      <Field
                        type="radio"
                        checked={values?.level === 'SITE'}
                        value="SITE"
                        name="level"
                        onClick={() => {
                          setFieldValue('level', 'SITE')
                          reFetchTriggerDetails(values?.triggerName?.value, 'SITE', setFieldValue)
                        }}
                      />
                      {translate('EMTF_MD_SITE')}
                    </label>
                  </div>
                </div>
              </div>
              <div className="form-group w50p">
                <label className="control-label">{translate('FILE_PLAN')}</label>
                <Select
                  name="filePlan"
                  value={values?.filePlan?.value}
                  placeholder={translate('SELECT_FILE_PLAN_PLACEHOLDER')}
                  options={transformToSelectOptions(options?.filePlans as any, 'id', 'name')}
                  isClearable={false}
                  searchable
                  hideSelectedOptions
                  onChange={(option: any) => {
                    setIsfilePlanInvalid(false)
                    setFieldValue('filePlan', option)
                    setSelectedFilters({
                      rule: [],
                      stage: [],
                      milestone: []
                    })
                  }}
                />
                {isSubmitted && errors?.filePlan && <div className="error">{translate(errors?.filePlan as any)}</div>}
                {isFilePlanInvalid && <p className="error">{translate('INVALID_FILEPLAN_ERROR_MSG')}</p>}
              </div>
              {eventTriggers?.length ? (
                <div>
                  <div className="field-wrapper d-flex justify-content-start">
                    <input
                      type="checkbox"
                      id="99c2a8b-dl9a-4132-b912-e63k3e555663"
                      name="autoApplyPackage"
                      className="mt0 mr4 cursor-pointer"
                      checked={values?.autoApplyPackage}
                      onChange={(event) => {
                        const { checked } = event.target
                        setFieldValue('autoApplyPackage', checked)
                      }}
                    />
                    <label htmlFor="99c2a8b-dl9a-4132-b912-e63k3e555663" className="mb0 cursor-pointer">
                      <div className="chkbc"></div>
                      {translate('AUTO_APPLY_PACKAGE')}
                    </label>
                  </div>
                  {values?.autoApplyPackage ? (
                    <div className="ml10">
                      <div className="field-wrapper">
                        <label className="half-width control-label">{translate('AUTO_APPLY_PACKAGE_TRIGGER')}</label>
                        <Select
                          name="triggerName"
                          value={values?.triggerName?.value}
                          placeholder={translate('AUTO_APPLY_PACKAGE_SELECT_TRIGGER_PH')}
                          options={triggersNames || []}
                          isClearable={false}
                          searchable
                          hideSelectedOptions
                          onChange={(option: any) => {
                            setTimeout(() => {
                              setFieldValue('triggerName', option)
                              setFieldValue('triggerDetails', null)
                            }, 0)
                            onTriggerNameChange(option?.value, values?.level)
                          }}
                        />
                        {isSubmitted && errors?.triggerName && (
                          <div className="error">{translate(errors?.triggerName)}</div>
                        )}
                      </div>
                      {values?.triggerName?.value ? (
                        <div className="ml10">
                          <div className="field-wrapper">
                            <label className="half-width control-label">
                              {translate(getSubTriggerNameOrPH(values?.triggerName?.value, false))}
                            </label>
                            <Select
                              name="triggerDetails"
                              value={
                                values?.triggerName?.value === TRIGGER.TEAMS ||
                                values?.triggerName?.value === TRIGGER.MILESTONES
                                  ? Array.isArray(values?.triggerDetails)
                                    ? (values?.triggerDetails as any[])?.map((item: any) => item?.value)
                                    : values?.triggerDetails
                                      ? [values?.triggerDetails]
                                      : []
                                  : values?.triggerDetails
                              }
                              placeholder={translate(getSubTriggerNameOrPH(values?.triggerName?.value, true))}
                              options={
                                values?.triggerName?.value === TRIGGER.TEAMS ||
                                values?.triggerName?.value === TRIGGER.MILESTONES
                                  ? processedTriggerDetailOptions(
                                      triggerOptions,
                                      values?.triggerDetails as Array<{ label: string; value: string }>
                                    )
                                  : triggerOptions
                              }
                              isClearable={true}
                              searchable
                              isMulti={
                                values?.triggerName?.value === TRIGGER.TEAMS ||
                                values?.triggerName?.value === TRIGGER.MILESTONES
                              }
                              hideSelectedOptions
                              onChange={(option: any) => {
                                setFieldValue(
                                  'triggerDetails',
                                  values?.triggerName?.value === TRIGGER.TEAMS ||
                                    values?.triggerName?.value === TRIGGER.MILESTONES
                                    ? option
                                    : option?.value
                                )
                              }}
                            />
                            {Array.isArray(values?.triggerDetails) &&
                            (values?.triggerDetails as any[])?.length >= MAX_SELECTION_ALLOWED ? (
                              <div className="error">{translate('TEAM_ROLES_MAX_SELECTION_ERR')}</div>
                            ) : null}
                            {isSubmitted && errors?.triggerDetails && (
                              <div className="error">{translate(String(errors?.triggerDetails))}</div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Form>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <button
            id="dd0c155c-a00c-4279-8a7e-a8a6fcd4ef1c"
            data-action={'cancel'}
            className="btn btn-link text-capitalize"
            onClick={doClose}
            type="button"
          >
            {translate('CANCEL')}
          </button>
          <button
            id="706301e1-c15d-4a0b-8de6-64416c47c1a0"
            data-action={'save'}
            onClick={gotoStep2}
            className="btn btn-primary text-capitalize"
            type="submit"
            disabled={isLoading}
          >
            {translate('NEXT')}
          </button>
        </div>
      </Modal.Footer>
    </>
  )
}

export default EditPackageStep1
